import React, { useMemo, useState } from "react";
import Icon from "./icons";
import Select from "../../../components/common/Select/Select";

/**
 * The editor's left panel. Two modes:
 *
 *   mode="drafts"  the resources being created on this page. One row per
 *                  resource, each becoming its own record when Create is
 *                  pressed. Used by the HTTP layout while creating.
 *   mode="list"    resources that already exist — the application's others of
 *                  the same type. Selecting one navigates to its own edit page.
 *                  Used by the DB layout, and by the HTTP layout while editing,
 *                  where only the one resource in the URL is being changed.
 *
 * Both are headed by the application name.
 */

/* The mark a name carries while it is being checked and once it has been: a
   spinner, then a tick if it is free or a cross if it is not. One component so
   the input and the row it sits in cannot disagree about which to show. */
function NameMark({ status }) {
  if (!status) return null;
  if (status.checking) return <span className="rsce-inline-spinner" aria-label="Checking name" />;
  if (status.error) return <span className="rsce-inline-cross" title={status.error} aria-label={status.error} />;
  if (status.available) {
    return <span className="rsce-inline-tick" title="That name is free" aria-label="Name is available" />;
  }
  return null;
}

/* Inline rename. Commits on Enter or blur, abandons on Escape — the same
   behaviour as renaming a board, so it needs no explaining. `status` puts the
   mark inside the box the name was typed into. */
function NameInput({ value, onCommit, onCancel, status }) {
  const [draft, setDraft] = useState(value);

  return (
    <span className="rsce-side-rename-wrap">
      <input
        className="rsce-side-rename"
        placeholder="Resource name"
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit(draft.trim() || value);
          if (e.key === "Escape") onCancel();
        }}
        onBlur={() => onCommit(draft.trim() || value)}
        onClick={(e) => e.stopPropagation()}
      />
      <NameMark status={status} />
    </span>
  );
}

export default function EditorSidebar({
  mode,
  apps = [],
  appId,
  onAppChange,
  /* drafts */
  drafts = [],
  selectedDraftId,
  onSelectDraft,
  onAddDraft,
  onRenameDraft,
  onDeleteDraft,
  /* list */
  items = [],
  selectedItemId,
  onSelectItem,
  onAddItem,
  onDeleteItem,
  /* Both default on, and are turned off while editing: the list is then the one
     resource that was opened, so there is nothing to search and adding would
     mean leaving it. */
  showSearch = true,
  canAdd = true,
  /* What the server has said about the selected draft's name:
     { checking, available }. Shown in the row it belongs to. */
  nameStatus = {},
}) {
  const [query, setQuery] = useState("");
  const [renaming, setRenaming] = useState(null); // draft id

  const appOptions = useMemo(() => apps.map((app) => ({ label: app.Name, value: app.ApplicationID })), [apps]);

  const q = query.trim().toLowerCase();
  const matches = (name) =>
    q === "" ||
    String(name || "")
      .toLowerCase()
      .includes(q);

  /* An unnamed draft is matched on the placeholder the row shows, so searching
     does not hide the row the user is in the middle of filling in. */
  const visibleDrafts = drafts.filter((d) => matches(d.name || "Untitled"));
  const visibleItems = items.filter((it) => matches(it.name));

  return (
    <aside className="rsce-side">
      {/* The application the resource belongs to. Every app the signed-in user
          can see is listed; changing it re-points the resource and, in list
          mode, changes which connections are shown below.

          Reads as the panel's title rather than as a form control — the border
          only appears on hover — but it is the same shared Select as the rest of
          the page, so its menu, sizing and click-away behave identically. */}
      <div className="rsce-side-head">
        <Select
          className="rsce-side-pick"
          options={appOptions}
          value={appId ? { label: "", value: appId } : null}
          placeholder="Select application"
          onChange={(option) => onAppChange(option.value)}
        />
      </div>

      <>
        {showSearch && (
          <div className="rsce-side-search">
            <span className="rsce-side-search-icon">{Icon.search}</span>
            <input
              type="text"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="rsce-side-search-input"
            />
          </div>
        )}

        <div className="rsce-side-body">
          {mode === "drafts" ? (
            <>
              {visibleDrafts.map((draft) => (
                /* The row, and under it whatever is wrong with its name. The
                     wrapper exists so the message sits beneath the field it is
                     about instead of across the form. */
                <div className="rsce-side-item" key={draft.id}>
                  <div
                    className={`rsce-ep-row ${draft.id === selectedDraftId ? "is-active" : ""}`}
                    onClick={() => onSelectDraft(draft.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") onSelectDraft(draft.id);
                    }}
                  >
                    {/* A method badge for an HTTP draft; the database glyph for
                        a connection, which has no method. */}
                    {draft.method ? (
                      <span className={`rsce-method-badge rsce-method-${String(draft.method).toLowerCase()}`}>
                        {draft.method}
                      </span>
                    ) : (
                      <span className="rsce-item-icon">{Icon.database}</span>
                    )}

                    {/* A draft with no name yet opens straight into the input
                        when it is the one selected: the name is required and
                        this is the only place it is edited, so it should not
                        need finding behind a pencil. */}
                    {renaming === draft.id || (!draft.name && draft.id === selectedDraftId) ? (
                      <NameInput
                        value={draft.name}
                        status={draft.id === selectedDraftId ? nameStatus : null}
                        onCommit={(name) => {
                          onRenameDraft(draft.id, name);
                          setRenaming(null);
                        }}
                        onCancel={() => setRenaming(null)}
                      />
                    ) : (
                      /* Named or not, the row has to say something. An unnamed
                         draft cannot be created — the name is required — so the
                         placeholder is muted rather than looking like a name. */
                      <span className={`rsce-ep-name ${draft.name ? "" : "is-untitled"}`} title={draft.name || ""}>
                        {draft.name || "Untitled"}
                      </span>
                    )}

                    {/* The check usually finishes after the input has closed —
                        the request goes out on blur — so the answer has to carry
                        on in the row, or it would flash and vanish. */}
                    {draft.id === selectedDraftId && renaming !== draft.id && <NameMark status={nameStatus} />}

                    <span className="rsce-row-actions">
                      <button
                        type="button"
                        className="rsce-row-btn"
                        title="Rename resource"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenaming(draft.id);
                        }}
                      >
                        {Icon.pencil}
                      </button>
                      <button
                        type="button"
                        className="rsce-row-btn rsce-row-btn-danger"
                        title="Remove resource"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteDraft(draft.id);
                        }}
                      >
                        {Icon.close}
                      </button>
                    </span>
                  </div>

                  {draft.id === selectedDraftId && nameStatus.error && (
                    <div className="rsce-side-note">{nameStatus.error}</div>
                  )}
                </div>
              ))}

              {visibleDrafts.length === 0 && <div className="rsce-side-empty">Nothing matches “{query}”.</div>}

              <button type="button" className="rsce-side-add" onClick={onAddDraft}>
                {Icon.plus}
                Add New
              </button>
            </>
          ) : (
            <>
              {visibleItems.map((item) => (
                <div
                  className={`rsce-item-row ${item.id === selectedItemId ? "is-active" : ""}`}
                  key={item.id}
                  onClick={() => onSelectItem(item)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onSelectItem(item);
                  }}
                >
                  {/* A method badge for an HTTP resource, the database glyph
                        for a connection — the row says what kind of thing it is
                        without needing a second column for it. */}
                  {item.method ? (
                    <span className={`rsce-method-badge rsce-method-${String(item.method).toLowerCase()}`}>
                      {item.method}
                    </span>
                  ) : (
                    <span className="rsce-item-icon">{Icon.database}</span>
                  )}
                  {/* Truncated by CSS when the panel is too narrow for it, so
                        the full name is on the element for a hover. */}
                  <span className="rsce-item-name" title={item.name}>
                    {item.name}
                  </span>
                  <span className="rsce-row-actions">
                    <button
                      type="button"
                      className="rsce-row-btn"
                      title="Edit connection"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectItem(item);
                      }}
                    >
                      {Icon.pencil}
                    </button>
                    <button
                      type="button"
                      className="rsce-row-btn rsce-row-btn-danger"
                      title="Delete connection"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteItem(item);
                      }}
                    >
                      {Icon.trash}
                    </button>
                  </span>
                </div>
              ))}

              {visibleItems.length === 0 && (
                <div className="rsce-side-empty">
                  {items.length === 0 ? "No connections yet." : `Nothing matches “${query}”.`}
                </div>
              )}

              {canAdd && (
                <button type="button" className="rsce-side-add" onClick={onAddItem}>
                  {Icon.plus}
                  Add New
                </button>
              )}
            </>
          )}
        </div>
      </>
    </aside>
  );
}
